/**
 * Copyright 2025 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Marc Fischer, ISW University of Stuttgart (for umati and VDW e.V.)
 *
 * Here, we handle an MQTT connection (client). We use a React context to provide a single connection for all subcomponents.
 */

import type { IClientOptions, MqttClient } from 'mqtt';
import mqtt from 'mqtt';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

export interface MqttContextV3 {
  connectionState: string;
  client?: MqttClient;
}

export const MqttContext = createContext<MqttContextV3>({
  connectionState: 'Unset',
  client: undefined,
});

interface MqttConnectionProps {
  brokerUrl: string;
  username?: string;
  password?: string;
}

/**
 * Create the provider. This must be a top level component.
 */
export default function MqttProviderV3({
  brokerUrl,
  username,
  password,
  children,
}: React.PropsWithChildren<MqttConnectionProps>) {
  const [mqttClient, setMqttClient] = useState<MqttClient | undefined>();
  const [connStatus, setConnStatus] = useState<string>('Not created.');

  useEffect(() => {
    const mqttConnectOptions: IClientOptions = {
      username,
      password,
    };

    const connCl = mqtt.connect(brokerUrl, mqttConnectOptions);
    console.debug('MQTTV3 provider started');
    setMqttClient(connCl);
    setConnStatus('init');
    connCl.on('connect', () => {
      console.debug('MQTTV3 connect');
      setConnStatus('connect');
    });
    connCl.on('disconnect', () => {
      console.debug('MQTTV3disconnect');
      setConnStatus('disconnect');
    });
    connCl.on('error', (err) => {
      console.error('MQTTV3 connection error: ', err);
      connCl.end();
    });

    /// TODO: Use other Callbacks for status tracking

    return () => {
      if (connCl !== undefined) {
        console.debug('MQTTV3 clear');
        connCl.end();
        setConnStatus('Deleted');
        setMqttClient(undefined);
      }
    };
  }, [brokerUrl, username, password]);

  const mqttConntextValue: MqttContextV3 = {
    connectionState: connStatus,
    client: mqttClient,
  };

  return <MqttContext.Provider value={mqttConntextValue}>{children}</MqttContext.Provider>;
}

/**
 * Use the Mqtt client
 */
export const useMqttV3 = () => useContext(MqttContext);

export function useMqttV3ConnectionDetailedTxt() {
  const mqttv3 = useMqttV3();
  const msg: string[] = [];
  msg.push(`connStatus ${mqttv3.connectionState}`);
  msg.push(`connected ${mqttv3.client?.connected}`);
  msg.push(`disconnected ${mqttv3.client?.disconnected}`);

  return msg.join('\n');
}

function getRegexMatchTopic(topic: string): RegExp {
  const topicRegexPattern = topic.replaceAll('$', '\\$').replaceAll('+', '(?:[^/]*)').replaceAll('/#', '(?:|\\/.*)');
  return new RegExp(`^${topicRegexPattern}$`);
}

export function useMqttV3SubscriptionCallb(
  topic: string | string[],
  callb: (topic: string, value: Buffer) => void,
  delayMs = 0,
) {
  const mqttv3 = useMqttV3();

  const topicFilterCallb = useCallback(
    (receivedTopic: string, rcvPayload: Buffer) => {
      const topicAr = Array.isArray(topic) ? topic : [topic];
      /// todo check resubscriptions with same topic, verify that a subscription is not canceld, if any other hook expects updates
      const regexTopicsAr = topicAr.map(getRegexMatchTopic);
      for (const t of regexTopicsAr) {
        if (receivedTopic.match(t) !== null) {
          callb(receivedTopic, rcvPayload);
        }
      }
    },
    [callb, topic],
  );

  useEffect(() => {
    if (!mqttv3.client) return;
    const client = mqttv3.client;
    const mqttClientState = client as MqttClient & { disconnecting?: boolean };

    const handleMessage = (receivedTopic, payload) => {
      topicFilterCallb(receivedTopic, payload);
    };

    client.on('message', handleMessage);

    let unsubscribeFn: (() => void) | undefined;
    const doSubscribe = () => {
      console.log(`subscribe to ${topic}${delayMs > 0 ? ` (after ${delayMs}ms delay)` : ''}`);
      client.subscribe(topic, { qos: 1 }, (err) => {
        if (err) console.error(`Failed to subscribe to ${topic}:`, err);
      });
      unsubscribeFn = () => {
        console.log(`Subscription to ${topic} ended`);
        client.off('message', handleMessage);
        client.unsubscribe(topic);
      };
    };

    let timerId: ReturnType<typeof setTimeout> | undefined;
    if (delayMs > 0) {
      timerId = setTimeout(doSubscribe, delayMs);
    } else {
      doSubscribe();
    }

    // cleanup on unmount or topic change
    return () => {
      if (timerId !== undefined) clearTimeout(timerId);
      if (unsubscribeFn) unsubscribeFn();
      else {
        // Timer fired but subscription not yet set up — still remove listener
        console.log(`Subscription to ${topic} ended`);
        client.off('message', handleMessage);
      }

      if (mqttClientState.connected && !mqttClientState.disconnecting && !mqttClientState.disconnected) {
        client.unsubscribe(topic, (err) => {
          if (err) {
            console.warn(`Failed to unsubscribe from ${topic}:`, err);
          }
        });
      }
    };
  }, [mqttv3.client, topic, topicFilterCallb, delayMs]);
}
